"use client";

import { useEffect, useId, useRef, useState, type ReactNode } from "react";

import { cn } from "@/lib/utils";

type ExpandableTextProps = {
  children: ReactNode;
  /** Number of lines shown while collapsed. */
  lines?: 4 | 6 | 8 | 10;
  moreLabel?: string;
  lessLabel?: string;
  className?: string;
};

const clampClass: Record<NonNullable<ExpandableTextProps["lines"]>, string> = {
  4: "line-clamp-4",
  6: "line-clamp-6",
  8: "line-clamp-[8]",
  10: "line-clamp-[10]",
};

/**
 * Full text is always in the DOM (server-rendered, crawlable). Collapsed via
 * CSS line-clamp; the toggle only appears when the text actually overflows.
 */
export function ExpandableText({
  children,
  lines = 6,
  moreLabel = "Show more",
  lessLabel = "Show less",
  className,
}: ExpandableTextProps) {
  const id = useId();
  const ref = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const measure = () => {
      if (expanded) return;
      setOverflows(el.scrollHeight > el.clientHeight + 1);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [expanded]);

  return (
    <div className={className}>
      <div
        id={id}
        ref={ref}
        className={cn(!expanded && clampClass[lines])}
      >
        {children}
      </div>

      {(overflows || expanded) && (
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={id}
          onClick={() => setExpanded((v) => !v)}
          className="mt-3 inline-flex min-h-11 items-center text-sm font-semibold text-foreground underline underline-offset-4 decoration-foreground/30 transition-colors hover:decoration-foreground"
        >
          {expanded ? lessLabel : moreLabel}
        </button>
      )}
    </div>
  );
}

ExpandableText.displayName = "ExpandableText";
